import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
    Instagram,
    ArrowUp,
    ArrowRight,
    MapPin,
    Mail,
} from "lucide-react";
import { toast } from "sonner";
import { submitNewsletter } from "@/lib/api";
import { FadeUp } from "@/components/Reveal";

const EXPLORE = [
    { label: "Home", path: "/" },
    { label: "Studio", path: "/studio" },
    { label: "Services", path: "/services" },
    { label: "Portfolio", path: "/portfolio" },
    { label: "Contact", path: "/contact" },
];

const SOCIALS = [
    {
        icon: Instagram,
        label: "Instagram",
        href: "https://www.instagram.com/ate.teen_architecture/",
    },
];

export default function Footer() {
    const [email, setEmail] = useState("");
    const [sending, setSending] = useState(false);
    const navigate = useNavigate();

    const onSubscribe = async (e) => {
        e.preventDefault();
        if (sending || !email) return;
        setSending(true);
        try {
            const res = await submitNewsletter(email);
            toast.success(res.data?.message || "Subscribed.");
            setEmail("");
        } catch {
            toast.error("Could not subscribe — please try again.");
        } finally {
            setSending(false);
        }
    };

    return (
        <footer
            data-testid="footer"
            className="relative overflow-clip border-t border-white/[0.07] bg-onyx"
        >
            <div className="blueprint-grid pointer-events-none absolute inset-0 opacity-30" />
            <motion.div
                aria-hidden="true"
                className="absolute left-0 top-0 h-px w-full origin-left bg-gradient-to-r from-transparent via-gold to-transparent"
                initial={{ scaleX: 0 }}
                whileInView={{ scaleX: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 1.6, ease: [0.22, 1, 0.36, 1] }}
            />

            <div className="relative mx-auto max-w-7xl px-6 py-20 sm:px-10">
                <FadeUp>
                    <p className="font-display text-[11vw] font-black lowercase leading-none tracking-tight text-outline opacity-60 sm:text-[7vw]">
                        ate.teen studio
                    </p>
                </FadeUp>

                <div className="mt-16 grid gap-12 md:grid-cols-12">
                    <div className="md:col-span-4">
                        <div>
                            <img
                                src="/images/logo.jpg"
                                alt="ate.teen studio logo"
                                className="h-16 w-16 rounded-xl border border-gold/40 object-cover shadow-[0_0_44px_-6px_rgba(200,169,106,0.4)]"
                            />
                        </div>
                        <p className="mt-6 max-w-xs text-sm leading-relaxed text-ash">
                            Designing spaces that matter
                            <br />
                            <span className="text-gold">Kollam | Kochi</span>
                        </p>
                        <div className="mt-8 flex gap-3">
                            {SOCIALS.map(({ icon: Icon, label, href }) => (
                                <a
                                    key={label}
                                    data-testid={`social-${label.toLowerCase()}`}
                                    href={href}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    aria-label={label}
                                    className="group relative flex h-11 w-11 items-center justify-center rounded-full border border-gold bg-gold/10 text-gold shadow-[0_0_28px_-4px_rgba(200,169,106,0.55)] transition-all duration-300 hover:bg-gold hover:text-coal hover:shadow-[0_0_36px_-2px_rgba(200,169,106,0.85)]"
                                >
                                    <span
                                        aria-hidden="true"
                                        className="absolute inset-0 -z-10 rounded-full bg-gold/30 blur-md transition-opacity duration-300 group-hover:opacity-100"
                                    />
                                    <Icon size={18} strokeWidth={1.75} />
                                </a>
                            ))}
                        </div>
                    </div>

                    <div className="md:col-span-2">
                        <p className="text-[10px] font-semibold uppercase tracking-[0.35em] text-gold">
                            Explore
                        </p>
                        <ul className="mt-6 space-y-3">
                            {EXPLORE.map((l) => (
                                <li key={l.label}>
                                    <button
                                        data-testid={`footer-link-${l.label.toLowerCase()}`}
                                        onClick={() => navigate(l.path)}
                                        className="text-sm text-ash transition-colors duration-300 hover:text-white"
                                    >
                                        {l.label}
                                    </button>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div className="md:col-span-3">
                        <p className="text-[10px] font-semibold uppercase tracking-[0.35em] text-gold">
                            Studio
                        </p>
                        <ul className="mt-6 space-y-3 text-sm text-ash">
                            <li className="flex items-center gap-3">
                                <MapPin size={14} className="shrink-0 text-gold" />
                                <span>KV Nagar, Thoppilkadavu, Kaankathu Mukku, Kollam, Keralam 691012</span>
                            </li>
                            <li className="flex items-center gap-3">
                                <Mail size={14} className="shrink-0 text-gold" />
                                <a
                                    data-testid="footer-email-link"
                                    href="mailto:ate.teendesignstudio@gmail.com"
                                    className="whitespace-nowrap transition-colors duration-300 hover:text-gold"
                                >
                                    ate.teendesignstudio@gmail.com
                                </a>
                            </li>
                        </ul>
                    </div>

                    <div className="md:col-span-4">
                        <p className="text-[10px] font-semibold uppercase tracking-[0.35em] text-gold">
                            The Studio Journal
                        </p>
                        <p className="mt-6 text-sm leading-relaxed text-ash">
                            One essay a month on craft, sites and materials.
                            No noise.
                        </p>
                        <form
                            data-testid="newsletter-form"
                            onSubmit={onSubscribe}
                            className="mt-5 flex overflow-hidden rounded-full border border-white/10 bg-coal/60 focus-within:border-gold/60"
                        >
                            <input
                                data-testid="newsletter-email-input"
                                type="email"
                                required
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="you@example.com"
                                aria-label="Email for newsletter"
                                className="w-full bg-transparent px-5 py-3.5 text-sm text-white outline-none placeholder:text-ash/60"
                            />
                            <button
                                data-testid="newsletter-submit-button"
                                type="submit"
                                disabled={sending}
                                aria-label="Subscribe"
                                className="flex items-center gap-2 bg-gold px-5 text-coal transition-colors duration-300 hover:bg-white disabled:opacity-60"
                            >
                                {sending ? "…" : <ArrowRight size={16} />}
                            </button>
                        </form>
                    </div>
                </div>

                <div className="mt-20 flex flex-col items-start justify-between gap-6 border-t border-white/[0.07] pt-8 sm:flex-row sm:items-center">
                    <p className="text-xs tracking-[0.2em] text-ash/70">
                        © 2026 ate.teen studio. All rights reserved.
                    </p>
                    <button
                        data-testid="back-to-top-btn"
                        onClick={() => window.__lenis?.scrollTo(0)}
                        className="group flex items-center gap-3 text-[10px] font-semibold uppercase tracking-[0.35em] text-ash transition-colors duration-300 hover:text-gold"
                    >
                        Back to top
                        <span className="flex h-9 w-9 items-center justify-center rounded-full border border-white/10 transition-colors duration-300 group-hover:border-gold">
                            <ArrowUp
                                size={14}
                                className="transition-transform duration-300 group-hover:-translate-y-0.5"
                            />
                        </span>
                    </button>
                </div>
            </div>
        </footer>
    );
}
